// Library methods
import { Box } from "@mui/system";
import { useParams } from "react-router";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// Contexts
import { ReportDataContext } from "../../contexts/ReportDataContext";

// Styles
import { PageContainerWrapper } from "../../styles/muiStylesHelper";

// Components
import ReportPdf from "../../components/UI/Report";
import SnackbarMessage from "../../components/UI/SnackbarMessage";
import ReportPanel from "../../containers/Report/ReportPanel";

// Utilities
import useClinic from "../../hooks/useClinic";
import ExamTransferModal from "../../components/UI/ExamTransferModal";
import { ExamTransferContext } from "../../contexts/ExamTransferContext";
import { getPatients } from "../../services/Patient";
import { useReportGeneration } from "../../hooks/reports/useReportGeneration";
import {
  AllFileTypes,
  generateReportFile,
  getAccessInfo,
  getReportInfo,
} from "../../services/Exam";

const Report = () => {
  // router params
  const { patientId, examId } = useParams();
  // translation
  const { t } = useTranslation();
  // clinic context
  const { clinicId } = useClinic();
  // auth
  const { getAccessTokenSilently } = useAuth0();
  // exam transfer
  const { openExamTransferModal } = useContext(ExamTransferContext);
  // state
  const [patients, setPatients] = useState(null);

  const {
    isLoading,
    patientName,
    patient,
    exam,
    reportData,
    refetchComment,
    shouldDisplayReport,
  } = ReportPdf(false, clinicId, patientId, examId);

  const customGetAccessInfo = useCallback(
    async (fileType, signal) => {
      const token = await getAccessTokenSilently();
      return getAccessInfo(
        token,
        clinicId,
        patientId,
        examId,
        fileType,
        signal
      );
    },
    [getAccessTokenSilently, clinicId, patientId, examId]
  );

  const customGetReportInfo = useCallback(
    async (signal) => {
      const token = await getAccessTokenSilently();
      return getReportInfo(token, clinicId, patientId, examId, signal);
    },
    [getAccessTokenSilently, clinicId, patientId, examId]
  );

  const customGenerateReportFile = useCallback(
    async (type) => {
      const token = await getAccessTokenSilently();
      return generateReportFile(
        token,
        clinicId,
        patientId,
        examId,
        AllFileTypes[type]
      );
    },
    [getAccessTokenSilently, clinicId, patientId, examId]
  );

  const {
    downloadPdf,
    shouldDisplayDicomButton,
    shouldDisplayPngButton,
    fileInfo,
    reportInfoError,
    openFailedToast,
    setOpenFailedToast,
    getReportInfoData,
  } = useReportGeneration({
    getAccessInfo: customGetAccessInfo,
    generateReportFile: customGenerateReportFile,
    getReportInfo: customGetReportInfo,
  });

  const getPatientsCall = useCallback(
    async (signal) => {
      try {
        const token = await getAccessTokenSilently();
        const patientsList = await getPatients(token, clinicId, signal);
        setPatients(patientsList);
      } catch (e) {}
    },
    [clinicId, getAccessTokenSilently]
  );

  useEffect(() => {
    const controller = new AbortController();
    if (openExamTransferModal) {
      getPatientsCall(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [getPatientsCall, openExamTransferModal]);

  return (
    <ReportDataContext.Provider
      value={{
        isLoading,
        patientName,
        patient,
        exam,
        reportData,
        refetchComment,
        getReportInfoData,
        fetchInitialReport: customGetReportInfo,
      }}
    >
      <Box
        sx={() => PageContainerWrapper()}
        px={{ xs: 2, sm: 4, md: 8, lg: 12 }}
        mb={{ xs: 2 }}
      >
        <ReportPanel
          downloadPdf={downloadPdf}
          shouldDisplayReport={shouldDisplayReport}
          fileInfo={fileInfo}
          reportInfoError={reportInfoError}
          shouldDisplayDicomButton={shouldDisplayDicomButton}
          shouldDisplayPngButton={shouldDisplayPngButton}
        />
        {/* Edit exam success/failure toasts */}
        <SnackbarMessage
          open={openFailedToast}
          onClose={() => setOpenFailedToast(false)}
          success={false}
          text={t("pdf_download_failed")}
        />
      </Box>
      <ExamTransferModal
        currentPatient={patient}
        patientsOptions={patients}
        fromeWhere={`report`}
      />
    </ReportDataContext.Provider>
  );
};

export default Report;
